import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import {
  dismissWhatsAppQueueMessages,
  mergeWaQueueMessages,
  type WaQueueProcessPayload,
} from '../../lib/demWhatsAppQueue';
import type { DemWhatsAppQueueMessage } from '../../types/dem';

type WaQueueModalProps = {
  open: boolean;
  onClose: () => void;
  onProcess: (payload: WaQueueProcessPayload) => void;
  messages: DemWhatsAppQueueMessage[];
  loading?: boolean;
  error?: string | null;
  lastSync?: string;
  onReload: (silent?: boolean) => Promise<void>;
  onDismissed?: () => void;
};

function previewMessage(message: string): string {
  const oneLine = message.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= 90) return oneLine;
  return `${oneLine.slice(0, 87)}…`;
}

export function WaQueueModal({
  open,
  onClose,
  onProcess,
  messages,
  loading = false,
  error: externalError = null,
  lastSync = '—',
  onReload,
  onDismissed,
}: WaQueueModalProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const error = localError ?? externalError;

  useEffect(() => {
    if (!open) return;
    void onReload();
    const timer = window.setInterval(() => {
      void onReload(true);
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [open, onReload]);

  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setLocalError(null);
    }
  }, [open]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => messages.some((row) => row.id === id)));
  }, [messages]);

  const selectedMessages = useMemo(
    () => messages.filter((row) => selectedIds.includes(row.id)),
    [messages, selectedIds],
  );

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  const handleDismiss = async () => {
    if (selectedIds.length === 0) return;
    const ok = window.confirm(`Remove ${selectedIds.length} selected message(s) from the queue?`);
    if (!ok) return;
    setBusy(true);
    setLocalError(null);
    try {
      await dismissWhatsAppQueueMessages(selectedIds);
      setSelectedIds([]);
      await onReload(true);
      onDismissed?.();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Failed to dismiss messages.');
    } finally {
      setBusy(false);
    }
  };

  const handleProcess = () => {
    setLocalError(null);
    try {
      const payload = mergeWaQueueMessages(selectedMessages);
      if (!payload) {
        setLocalError('Select at least one message to process.');
        return;
      }
      onProcess(payload);
      onClose();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Failed to prepare messages.');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="WhatsApp incoming queue" orderDetail>
      <div className="dem-wa-queue">
        <div className="dem-wa-queue__toolbar">
          <div className="dem-wa-queue__actions">
            <button type="button" className="btn-secondary btn-sm" disabled={loading || busy} onClick={() => void onReload()}>
              Refresh
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm dem-wa-queue__dismiss"
              disabled={selectedIds.length === 0 || busy}
              onClick={() => void handleDismiss()}
            >
              Remove selected
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={selectedIds.length === 0 || busy}
              onClick={handleProcess}
            >
              Process WA order
            </button>
          </div>
          <p className="dem-wa-queue__sync">Last synced: {lastSync}</p>
        </div>

        {error ? <p className="alert alert-error">{error}</p> : null}

        <div className="dem-wa-queue__table-wrap">
          <div className="dem-wa-queue__head" aria-hidden>
            <span />
            <span>Received</span>
            <span>Customer / group</span>
            <span>Message preview</span>
            <span>Status</span>
          </div>

          {loading && messages.length === 0 ? (
            <p className="dem-wa-queue__empty">Loading queue…</p>
          ) : messages.length === 0 ? (
            <p className="dem-wa-queue__empty">No pending WhatsApp messages.</p>
          ) : (
            messages.map((row) => {
              const checked = selectedIds.includes(row.id);
              const isVoice =
                /\[Voice transcript/i.test(row.message) ||
                row.message.includes('[🎙️ Voice Note Captured]');
              return (
                <label
                  key={row.id}
                  className={`dem-wa-queue__row${checked ? ' dem-wa-queue__row--selected' : ''}`}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleRow(row.id)} />
                  <span>{row.created_at_display}</span>
                  <span>{row.customer_id}</span>
                  <span className={isVoice ? 'dem-wa-queue__voice' : undefined}>{previewMessage(row.message)}</span>
                  <span>{row.status}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}
